import { useRef, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import EnterCard from "./components/EnterCard";
import TimeSpentCard from "./components/TimeSpentCard";
import { ClockOutline, LinkIcon } from "assets/icons";
import useTranslation from "hooks/useTranslation";
import styles from "./styles.module.scss";
import { getDecodedURL, getDomainFromUrl } from "utils/url";
import { getElapsedTime } from "utils/date";
import {
  getFormatedTotalFromTimeElapsedObject,
  getTotalElapsedTimeInMs,
} from "./components/Utils";

const getLocale = ({ settingsData }) => settingsData.locale;
const getData = ({ roadmapData }) => roadmapData.roadmap.data;
const getUser = ({ chatsData }) => chatsData.selectedConversation?.user;

function UserRoadmap({ userName }) {
  const [coordinates, setCoordinates] = useState([]);
  const [pagesData, setPagesData] = useState([]);

  const locale = useSelector(getLocale);
  const data = useSelector(getData);
  const user = useSelector(getUser);

  const { getTranslation } = useTranslation("CHAT");
  const { t } = getTranslation();

  const cardsRef = useRef([]);
  const svgRef = useRef();

  const firstCard = pagesData[0];
  const lastCard = pagesData[pagesData.length - 1];

  const totalTimeSpent = pagesData.length
    ? getFormatedTotalFromTimeElapsedObject(
        getElapsedTime(
          new Date(0),
          new Date(getTotalElapsedTimeInMs(pagesData))
        )
      )
    : 0;

  // returns arrow element based on start and end point
  const getArrowElement = (points) => {
    if (points) {
      return (
        <polyline
          points={points.join(" ")}
          fill="none"
          stroke="grey"
          marker-start="url(#dot)"
          marker-end="url(#arrow)"
        />
      );
    }
  };

  const getCoordinatesAccordingToSvg = (point) => {
    const bounds = svgRef.current.getBoundingClientRect();
    const { x: svgX, y: svgY } = bounds;

    return { x: point.x - svgX, y: point.y - svgY };
  };

  // modifies and applies changes on the start and end points coordinates
  const getPolylinePoints = (start, end) => {
    // alignment with svg coordinates
    const { x: x1, y: y1 } = getCoordinatesAccordingToSvg(start.coordinates);
    const { x: x2, y: y2 } = getCoordinatesAccordingToSvg(end.coordinates);

    const { height: el1H, width: el1W } = start.element.getBoundingClientRect();
    const { height: el2H, width: el2W } = end.element.getBoundingClientRect();

    const markerYOffset = 10;
    const markerXOffset = 10;

    // horizontal lines
    if (Math.abs(y1 - y2) < 100) {
      const startxOffset =
        locale === "fa" ? -markerXOffset : markerXOffset + el2W;
      const endxOffset = locale === "fa" ? el2W : 0;

      return [
        `${x1 + startxOffset},${y1 + el1H / 2}`,
        `${x2 + endxOffset},${y2 + el2H / 2}`,
      ];
    }

    // vertical lines with distance in x axis
    return [
      `${x1 + el1W / 2},${y1 + el1H + markerYOffset}`,
      `${x1 + el1W / 2},${y1 + el1H + 40}`,
      `${x2 + el1W / 2},${y1 + el1H + 40}`,
      `${x2 + el1W / 2},${y2}`,
    ];
  };

  const getPathGuideLines = () => {
    if (!coordinates.length) return "";

    const pathCoordinates = coordinates.map((item, index) => {
      if (index >= coordinates.length - 1) return "";

      const startPoint = item;
      const endPoint = coordinates[index + 1];

      const points = getPolylinePoints(
        { coordinates: startPoint, element: cardsRef.current[index] },
        { coordinates: endPoint, element: cardsRef.current[index + 1] }
      );

      return getArrowElement(points);
    });
    return pathCoordinates;
  };

  // in addition to preventing extra re-renders this memoize is to prvent messing up guides in re-renders and prevent
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pathGuideLines = useMemo(getPathGuideLines, [coordinates]);

  const renderTimeSpentCards = () => {
    if (!pagesData.length) return <></>;

    return pagesData.map((item, index) => (
      <TimeSpentCard
        id={index + 1}
        key={index + 1}
        data={item}
        isLastCard={index === pagesData.length - 1}
        ref={cardsRef}
        locale={locale}
      />
    ));
  };

  const updateCoordinates = (el, id) => {
    if (el) {
      const bounds = el.getBoundingClientRect();
      const { x, y } = bounds;

      setCoordinates((prev) => {
        const newCoordinates = [...prev];
        newCoordinates[id] = { x, y };
        return newCoordinates;
      });
    }
  };

  useEffect(() => {
    if (data.pages.length && !pagesData.length) {
      setPagesData(data.pages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (pagesData.length) {
      cardsRef.current = cardsRef.current.slice(0, pagesData.length + 1);
    }
  }, [pagesData]);

  useEffect(() => {
    if (!cardsRef.current.length) return;

    for (let i = 0; i < cardsRef.current.length; i++) {
      updateCoordinates(cardsRef.current[i], i);
    }
  }, [cardsRef, pagesData]);

  // updating coordinates as window resizes
  useEffect(() => {
    const updateCoordinate = () => {
      for (let i = 0; i < cardsRef.current.length; i++) {
        updateCoordinates(cardsRef.current[i], i);
      }
    };

    window.addEventListener("resize", updateCoordinate);

    return () => window.removeEventListener("resize", updateCoordinate);
  });

  if (!pagesData.length) return <></>;

  return (
    <div>
      {/* intro */}
      {/* TODO: make multilingual when english translation provided */}
      <div className={styles.intro}>
        <h2>{t("ROADMAP_MODAL").TITLE}</h2>
        <div className={styles.description}>
          <p>
            به وسیله‌ی این قابلیت شما می‌تونید تمامی اعمال حضور کاربر در صفحات
            مختلف وبسایتتون رو ببینید. همچنین می‌تونید رصد کنید که هر کاربر
            دقیقا چه زمانی در هر صفحه توقف داشته تا بتونید به راحتی بهش کمک
            کنید.
          </p>
          <p>
            نقشه سفر مشتری بهتون کمک میکنه که همیشه یک قدم جلوتر از مشتری باشید
            و وضعیت و‌ زمان حضور اون توی صفحات مختلف وبسایتتون رو بررسی کنید.
          </p>
        </div>
      </div>

      <div className={styles.roadmap}>
        <div className={styles.cards}>
          <svg
            className={styles.guide_lines_svg}
            ref={svgRef}
            xmlns="http://www.w3.org/2000/svg"
          >
            <marker
              id="dot"
              viewBox="0 0 10 10"
              refX="1"
              refY="5"
              markerWidth="5"
              markerHeight="5"
              orient="auto-start-reverse"
            >
              <circle
                cx="5"
                cy="5"
                r="4"
                fill="none"
                stroke="#791469"
                stroke-width={2}
              />
            </marker>

            <marker
              id="arrow"
              viewBox="0 0 10 10"
              refX="10"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
              fill="#791469"
            >
              <path
                stroke="#791469"
                fill="none"
                stroke-width={1.5}
                d="M 0 0 L 10 5 L 0 10"
              />
            </marker>
            {pathGuideLines}
          </svg>

          <div className={styles.time_spent_cards}>
            <div className={styles.enter_card__wrapper}>
              <EnterCard
                onSetCoordinates={setCoordinates}
                id={0}
                user={user}
                userName={userName}
                data={firstCard}
                ref={cardsRef}
                locale={locale}
              />
            </div>
            {renderTimeSpentCards()}
          </div>
        </div>
        {/* total */}
        <div className={styles.total_report}>
          <div className={styles.total}>
            <img className={styles.clock_icon} src={ClockOutline} alt="" />
            <div className={styles.total__label}>
              <span>{t("ROADMAP_MODAL").TOTAL_TIME}</span>:
            </div>
            <span className={styles.total__value}>{totalTimeSpent}</span>
          </div>
          <div className={styles.total}>
            <div className={styles.total__label}>
              <div className="flex">
                <img className={styles.link_icon} src={LinkIcon} alt="" />
                <span>{t("ROADMAP_MODAL").LAST_PAGE}</span>: {"  "}
              </div>
            </div>
            <a
              className={styles.total__value}
              target="_blank"
              rel="noreferrer"
              href={lastCard.url}
              title={lastCard.url}
            >
              {getDomainFromUrl(getDecodedURL(getDecodedURL(lastCard.url)))}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserRoadmap;
